import { motion, AnimatePresence } from 'framer-motion'
import { useEffect, useRef, useState } from 'react'
import { QRCodeCanvas } from 'qrcode.react'
import type { LiveSession, LeaderboardState, OvertakeEvent, ComebackEvent, ShoutoutEvent, LeaderboardEntry, IntervalTimerState } from '@gymboard/types'
import TeamBattleBar from '../components/leaderboard/TeamBattleBar'
import ShoutoutToast from '../components/ShoutoutToast'
import IntervalPill from '../components/spotlight/IntervalPill'
import SpotlightStage from '../components/spotlight/SpotlightStage'
import ChorusList from '../components/spotlight/ChorusList'
import '../components/spotlight/spotlight.css'
import { getTenantInfo } from '../lib/storage'
import { emit } from '../lib/events'
import { useHandoff } from '../components/spotlight/useHandoff'

interface Props {
  session: LiveSession | null
  sessionStatus: string
  leaderboard: LeaderboardState | null
  qrToken: string | null
  qrSignature: string | null
  qrExpiresAt: number | null
  /** Kept for the App contract; the Spotlight stage tells overtakes and
   *  comebacks (the server puts them on stage). */
  overtakeEvent: OvertakeEvent | null
  comebackEvent: ComebackEvent | null
  shoutoutEvent: ShoutoutEvent | null
  timer?: IntervalTimerState | null
  clockOffsetMs?: number
  isConnected: boolean
  isReconnecting: boolean
  elapsedMs: number
}


// ─── Self-contained session timer ─────────────────────────────────────────────
function useSessionTimer(session: LiveSession | null, sessionStatus: string, seedMs: number): number {
  const [elapsedMs, setElapsedMs] = useState(seedMs)
  const seedMsRef = useRef(seedMs)
  seedMsRef.current = seedMs

  useEffect(() => {
    const isPaused = sessionStatus === 'PAUSED'
    const isLive = sessionStatus === 'LIVE'
    if ((!isLive && !isPaused) || !session?.startedAt) {
      setElapsedMs(seedMsRef.current)
      return
    }
    const started = new Date(session.startedAt as string).getTime()
    const pausedExtra = (session.totalPausedMs as number) ?? 0
    if (isPaused) {
      const pausedAt = session.pausedAt ? new Date(session.pausedAt as string).getTime() : Date.now()
      setElapsedMs(Math.max(0, pausedAt - started - pausedExtra))
      return
    }
    const tick = () => setElapsedMs(Math.max(0, Date.now() - started - pausedExtra))
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, sessionStatus])

  return elapsedMs
}

function formatElapsedTimer(ms: number): string {
  const totalSecs = Math.floor(ms / 1000)
  return `${Math.floor(totalSecs / 60).toString().padStart(2, '0')}:${(totalSecs % 60).toString().padStart(2, '0')}`
}

// ─── Final-30s crescendo ─────────────────────────────────────────────────────
// 0..1 intensity over the last 30 s of a timed class; fires `tv:final-30s`
// once so soundboard / LED listeners can react.
function useFinalCrescendo(session: LiveSession | null, elapsedMs: number, sessionStatus: string): number {
  const firedRef = useRef(false)
  const sessionIdRef = useRef<string | null>(null)
  const durationMins =
    (session?.classInstance as any)?.durationMins ?? (session?.classInstance as any)?.template?.durationMins ?? null
  const totalMs = durationMins ? durationMins * 60_000 : null
  const remainMs = totalMs ? Math.max(0, totalMs - elapsedMs) : null

  useEffect(() => {
    const id = (session?.id as string | undefined) ?? null
    if (id !== sessionIdRef.current) {
      sessionIdRef.current = id
      firedRef.current = false
    }
  }, [session?.id])

  useEffect(() => {
    if (sessionStatus !== 'LIVE' || remainMs === null) return
    if (!firedRef.current && remainMs <= 30_000 && remainMs > 0 && session?.id) {
      firedRef.current = true
      emit('tv:final-30s', { sessionId: session.id as string })
    }
  }, [remainMs, sessionStatus, session?.id])

  if (sessionStatus !== 'LIVE' || remainMs === null || remainMs > 30_000 || remainMs <= 0) return 0
  return Math.min(1, (30_000 - remainMs) / 30_000)
}

function groupStats(entries: LeaderboardEntry[]) {
  const withHr = entries.filter((e) => e.isActive && e.heartRate)
  return {
    kcal: Math.round(entries.reduce((s, e) => s + (e.calories ?? 0), 0)),
    avgHr: withHr.length ? Math.round(withHr.reduce((s, e) => s + (e.heartRate ?? 0), 0) / withHr.length) : 0,
  }
}

// ─── QR check-in, docked under the leaderboard ───────────────────────────────
function QrCheckin({ token, signature, expiresAt }: { token: string; signature: string; expiresAt: number }) {
  const [secondsLeft, setSecondsLeft] = useState(0)
  useEffect(() => {
    const tick = () => setSecondsLeft(Math.max(0, Math.round((expiresAt - Date.now()) / 1000)))
    tick()
    const id = setInterval(tick, 500)
    return () => clearInterval(id)
  }, [expiresAt])
  const size = Math.round(Math.min(window.innerWidth * 0.065, 150))
  return (
    <div className="sp-qr">
      <QRCodeCanvas value={JSON.stringify({ token, signature })} size={size} bgColor="#ffffff" fgColor="#000000" level="M" includeMargin />
      <div>
        <p>Late? Scan to join the class</p>
        <small className={secondsLeft <= 5 ? 'soon' : ''}>New code in {secondsLeft}s</small>
      </div>
    </div>
  )
}

// ─── Main screen ──────────────────────────────────────────────────────────────
export default function LiveLeaderboardScreen({
  session,
  sessionStatus,
  leaderboard,
  qrToken,
  qrSignature,
  qrExpiresAt,
  shoutoutEvent,
  timer = null,
  clockOffsetMs = 0,
  isConnected,
  isReconnecting,
  elapsedMs: seedMs,
}: Props) {
  const tenantInfo = getTenantInfo()
  const entries = leaderboard?.entries ?? []
  const scoringMode = leaderboard?.scoringMode ?? session?.scoringMode ?? 'EFFORT_SCORE'
  const isPaused = sessionStatus === 'PAUSED'
  const elapsedMs = useSessionTimer(session, sessionStatus, seedMs)
  const crescendo = useFinalCrescendo(session, elapsedMs, sessionStatus)

  const className = session?.classInstance?.template?.name ?? 'Live class'
  const coachName = session?.classInstance?.coach
    ? `${(session.classInstance.coach as any).firstName} ${(session.classInstance.coach as any).lastName}`
    : null
  const roomName = (session?.classInstance as any)?.room?.name ?? null
  const scoringLabel = scoringMode === 'CALORIES_ONLY' ? 'Calories' : scoringMode === 'HR_ZONE_POINTS' ? 'Heart-rate zones' : 'Effort score'
  const { kcal, avgHr } = groupStats(entries)

  // Spotlight comes from the server so the coach console and phones agree.
  // Older API frames have no spotlight: fall back to the leader.
  const spotlight =
    leaderboard?.spotlight ??
    (entries[0] ? { athleteId: entries[0].athleteId, reason: 'top' as const, rank: 1, since: 0 } : null)
  const hero = spotlight ? entries.find((e) => e.athleteId === spotlight.athleteId) ?? null : null
  const swapKey = spotlight ? `${spotlight.athleteId}:${spotlight.since}` : 'none'

  // The hero's race, for the stage: the gap to the place ahead, or the lead.
  let story: { lead: string; rest: string } | null = null
  if (hero) {
    const i = entries.findIndex((e) => e.athleteId === hero.athleteId)
    const ahead = i > 0 ? entries[i - 1] : null
    const next = i === 0 ? entries[1] : null
    if (ahead) {
      const gap = Math.max(1, Math.ceil(ahead.score - hero.score))
      story = { lead: `${gap} ${gap === 1 ? 'pt' : 'pts'}`, rest: `behind ${ahead.athleteName}` }
    } else if (next) {
      const gap = Math.max(1, Math.round(hero.score - next.score))
      story = { lead: `Leading by ${gap} ${gap === 1 ? 'pt' : 'pts'}`, rest: `over ${next.athleteName}` }
    }
  }

  const rootRef = useRef<HTMLDivElement>(null)
  const nameRef = useRef<HTMLDivElement>(null)
  useHandoff(swapKey, hero ? hero.athleteId : null, rootRef, nameRef)

  const hasQr = !!(qrToken && qrSignature && qrExpiresAt)
  const statusColor = isPaused ? '#FFD60A' : '#30D158'

  return (
    <div ref={rootRef} className="w-screen h-screen relative bg-[#0E1022] overflow-hidden flex flex-col">
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <header className="sp-head">
        <svg className="mark" viewBox="0 0 64 64" aria-hidden="true">
          <g transform="translate(4 4)">
            <path d="M14.9 45.2A24 24 0 1 1 41.1 45.2" fill="none" stroke="#F1EEE8" strokeWidth="5.5" strokeLinecap="round" />
            <path d="M28 12c5 7.5 11 12 11 20.5A11 11 0 0 1 17 32.5c0-5 2.6-8.4 5.3-11.3 1 3.3 2.8 5.3 5.2 6.3C28.4 21.8 27.5 16.8 28 12Z" fill="#C93A30" />
          </g>
        </svg>
        <span className="sp-status" style={{ color: statusColor, background: isPaused ? 'rgba(255,214,10,0.12)' : 'rgba(48,209,88,0.12)' }}>
          <i />
          {isPaused ? 'Paused' : 'Live'}
        </span>
        <div className="sp-class">
          <h1>{className}</h1>
          <p>{[coachName, roomName, scoringLabel].filter(Boolean).join('  /  ')}</p>
        </div>
        <IntervalPill timer={timer} clockOffsetMs={clockOffsetMs} />
        <div className="sp-clock">
          <b style={{ color: isPaused ? '#FFD60A' : crescendo > 0 ? '#FF7A6E' : '#F1EEE8' }}>{formatElapsedTimer(elapsedMs)}</b>
          <span>{isPaused ? 'Paused' : crescendo > 0 ? 'Final push' : 'Elapsed'}</span>
        </div>
        {(kcal > 0 || avgHr > 0) && (
          <div className="sp-meta">
            {avgHr > 0 && <b>{avgHr} BPM</b>}
            {kcal > 0 && <span>{kcal.toLocaleString()} kcal together</span>}
          </div>
        )}
        <div className="sp-meta">
          <b>{tenantInfo?.name ?? 'Agaru'}</b>
          {(!isConnected || isReconnecting) && (
            <span className="warn">{isReconnecting ? 'Reconnecting' : 'Offline'}</span>
          )}
        </div>
      </header>

      {/* ── Team battle (only while the coach has teams on) ───────────────── */}
      {leaderboard?.teams && leaderboard.teams.length > 0 && <TeamBattleBar teams={leaderboard.teams} />}

      {/* ── Stage + chorus ───────────────────────────────────────────────── */}
      <div className="sp-main">
        <SpotlightStage
          ref={nameRef}
          spotlight={spotlight}
          hero={hero}
          teams={leaderboard?.teams}
          swapKey={swapKey}
          hasQr={hasQr}
          story={story}
        />
        <div className="flex-1 min-w-0 flex flex-col">
          {entries.length > 0 && <ChorusList entries={entries} spotlightId={hero?.athleteId ?? null} />}
          {hasQr && !isPaused && <QrCheckin token={qrToken!} signature={qrSignature!} expiresAt={qrExpiresAt!} />}
        </div>
      </div>

      {/* ── Pause ────────────────────────────────────────────────────────── */}
      <AnimatePresence>
        {isPaused && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="absolute inset-0 pointer-events-none z-30 flex items-end justify-center pb-[5vh]"
            style={{ background: 'rgba(14,16,34,0.45)' }}
          >
            <div className="flex items-center gap-[0.8vw] rounded-full px-[2vw] py-[0.8vw]" style={{ background: '#252945', boxShadow: 'inset 0 0 0 1px rgba(255,214,10,0.35)' }}>
              <svg viewBox="0 0 24 24" className="w-[1.4vw] h-[1.4vw]" aria-hidden="true">
                <path d="M8 5v14M16 5v14" stroke="#FFD60A" strokeWidth="2.8" strokeLinecap="round" />
              </svg>
              <span style={{ color: '#FFD60A', font: '600 1.4vw/1 "Readex Pro", system-ui, sans-serif' }}>Class paused</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Final-30s vignette ───────────────────────────────────────────── */}
      <AnimatePresence>
        {crescendo > 0 && !isPaused && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: crescendo * 0.5 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 pointer-events-none z-30"
            style={{ background: 'radial-gradient(ellipse 120% 80% at 50% 50%, transparent 40%, rgba(201,58,48,0.4) 100%)' }}
          />
        )}
      </AnimatePresence>

      {/* ── Overlays ─────────────────────────────────────────────────────── */}
      <ShoutoutToast event={shoutoutEvent} />
    </div>
  )
}
